/**
 * enrollContract — wire shaping + error classification for the two-step SIWE
 * enrollment flow. Pure functions only; no network, no React.
 *
 *   1. GET  /enroll/nonce?wallet=0x…  → { nonce, message, expiresAtMs }
 *   2. wallet personal_signs `message` VERBATIM (free, no gas, no transaction)
 *   3. POST /enroll { wallet, signature, nonce }
 *                                    → { userId, webhookToken, webhookUrl, ... }
 *
 * Errors: 400 = missing/invalid signature or expired nonce.
 *         409 = wallet already enrolled to an account (support only, never
 *               auto-transferred).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CONTRACT CONFIRMED 2026-08-13 (Gven): the above IS the live deployed engine.
 * The older `POST /enroll/challenge` → `challengeId` → 428 flow is stale
 * scaffolding, NOT a second dialect to stay compatible with. This module
 * therefore targets the live contract only — it does not read `challengeId`
 * off a nonce response and does not treat 428 as "signature required".
 *
 * RESOLVED 2026-08-13: SignalsTab.js has been migrated onto this flow (via
 * enrollFlow.js) and the legacy `getEnrollChallenge()` / bare `enroll()` exports
 * have been deleted from `services/signalsApi.js`. There is no longer a second
 * enrollment dialect anywhere in the client.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Classified enrollment failure kinds. */
export const ENROLL_ERROR = Object.freeze({
  ALREADY_ENROLLED: "already-enrolled",
  SIGNATURE_REQUIRED: "signature-required",
  SIGNATURE_REJECTED: "signature-rejected",
  NONCE_EXPIRED: "nonce-expired",
  UNSUPPORTED: "unsupported",
  NOT_CONFIGURED: "not-configured",
  AUTH: "auth",
  NETWORK: "network",
  OTHER: "other",
});

/**
 * Normalize GET /enroll/nonce into { nonce, message, expiresAtMs }.
 *
 * Reads the live contract's field names ONLY. A response missing either piece
 * is treated as unusable rather than salvaged — see isSignable().
 *
 * @returns {{nonce: ?string, message: ?string, expiresAtMs: ?number}}
 * Missing pieces come back null — the caller refuses to prompt for a signature
 * on an incomplete challenge rather than signing something it invented.
 */
export function normalizeNonce(res) {
  if (!res || typeof res !== "object") return { nonce: null, message: null, expiresAtMs: null };
  const nonce = res.nonce ?? null;
  const message = res.message ?? null;
  const expiresAtMs = Number.isFinite(res.expiresAtMs) ? res.expiresAtMs : null;
  return {
    nonce: nonce == null ? null : String(nonce),
    message: message == null ? null : String(message),
    expiresAtMs,
  };
}

/** A challenge is only signable if we have both the opaque nonce and the exact
 *  message the server built. We never reconstruct or reformat the message. */
export function isSignable(challenge) {
  return Boolean(challenge && challenge.nonce && challenge.message);
}

/** Build the POST /enroll body. Sends exactly the three contract fields. */
export function enrollBody({ wallet, signature, nonce }) {
  return { wallet, signature, nonce };
}

/* ─── Error classification ─────────────────────────────────────────────── */

// EIP-1193 user-rejected-request, plus the message wallets actually show.
const isUserRejection = (err) =>
  err?.code === 4001 ||
  err?.name === "UserRejectedRequestError" ||
  /user rejected|user denied|rejected the request/i.test(err?.message || "");

const statusOf = (err) => {
  if (Number.isFinite(err?.status)) return err.status;
  const m = /^HTTP_(\d{3})$/.exec(err?.code || "");
  return m ? Number(m[1]) : null;
};

/**
 * Map any failure from the enrollment flow onto a member-facing state.
 *
 * @returns {{kind: string, message: string, showSupport: boolean, retryable: boolean}}
 *   `message` is compliance-safe copy: it explains what happened and what to do,
 *   and never mentions money, returns, or what anyone should do with funds.
 */
export function classifyEnrollError(err) {
  if (!err) return null;

  if (isUserRejection(err)) {
    return {
      kind: ENROLL_ERROR.SIGNATURE_REJECTED,
      message:
        "You cancelled the signature request in your wallet, so nothing was enrolled. The signature only proves you control this wallet — it's free, it isn't a transaction, and it gives no one permission to move your funds.",
      showSupport: false,
      retryable: true,
    };
  }

  if (err.code === "NOT_CONFIGURED") {
    return {
      kind: ENROLL_ERROR.NOT_CONFIGURED,
      message:
        "The Strategy Automation backend isn't connected in this environment, so enrollment can't run here.",
      showSupport: false,
      retryable: false,
    };
  }

  if (err.code === "UNSUPPORTED_CLIENT") {
    return {
      kind: ENROLL_ERROR.UNSUPPORTED,
      message:
        "Enrollment needs the signalsApi SIWE client (patches/signalsApi-enroll-nonce.patch) applied. Nothing was sent.",
      showSupport: false,
      retryable: false,
    };
  }

  if (err.code === "AUTH" || err.code === "NO_AUTH") {
    return {
      kind: ENROLL_ERROR.AUTH,
      message:
        "Your session isn't authorized for Strategy Automation. Try signing out and back in, then start enrollment again.",
      showSupport: false,
      retryable: true,
    };
  }

  if (err.code === "NETWORK") {
    return {
      kind: ENROLL_ERROR.NETWORK,
      message:
        "Couldn't reach the Strategy Automation backend. Check your connection and try again — nothing was enrolled.",
      showSupport: false,
      retryable: true,
    };
  }

  const status = statusOf(err);

  // 409 — this wallet belongs to an enrollment already. Deliberately does NOT
  // say whose: that would leak account linkage between members.
  if (status === 409) {
    return {
      kind: ENROLL_ERROR.ALREADY_ENROLLED,
      message:
        "This wallet is already enrolled. A wallet can only be linked to one account, and for security we can't move an enrollment automatically. If this is your wallet and you expected it to work here, contact support and we'll sort it out.",
      showSupport: true,
      retryable: false,
    };
  }

  // 400 — the engine wants the wallet-ownership signature and didn't get a
  // valid one. (The legacy 428 is NOT handled: the live engine doesn't send it,
  // and mapping it here would paper over a stale client rather than surface it.)
  if (status === 400) {
    // A nonce that timed out is the common, harmless case — name it separately
    // so the member gets "try again" rather than a scary validation error.
    if (/expired|stale|no longer valid/i.test(err.message || "")) {
      return {
        kind: ENROLL_ERROR.NONCE_EXPIRED,
        message:
          "That sign-in request timed out before it was signed. Start enrollment again to get a fresh one.",
        showSupport: false,
        retryable: true,
      };
    }
    return {
      kind: ENROLL_ERROR.SIGNATURE_REQUIRED,
      message:
        "Enrollment needs a wallet signature to prove you control this address, and the request arrived without one. Start enrollment again and approve the signature request in your wallet — it's free and it isn't a transaction.",
      showSupport: false,
      retryable: true,
    };
  }

  return {
    kind: ENROLL_ERROR.OTHER,
    message:
      err.message ||
      "Enrollment didn't complete. Nothing was changed — please try again.",
    showSupport: false,
    retryable: true,
  };
}
